import {
  Box,
  Button,
  Chip,
  Collapse,
  Container,
  Divider,
  Fade,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Slide,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Dispatch,
  FC,
  MouseEvent,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Tabbar from "../../../../Components/Tabbar";

import { KeyOutlined, LockOutlined, MoreHoriz } from "@mui/icons-material";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import TroubleshootIcon from "@mui/icons-material/Troubleshoot";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import AssetHeroBypassingCard from "../../../../Components/Assets/AssetHeroBypassingCard/asset-hero-bypassing-card";
import AssetHeroDeletionCard from "../../../../Components/Assets/AssetHeroDeletionCard/asset-hero-deletion-card";
import AssetHeroPendingQcCard from "../../../../Components/Assets/AssetHeroPendingQcCard/asset-hero-pending-qc-card";
import AssetHeroRejectionCard from "../../../../Components/Assets/AssetHeroRejectionCard/asset-hero-rejection-card";
import AssetHeroRequestCard from "../../../../Components/Assets/AssetHeroRequestCard/asset-hero-request-card";
import AssetThumbnail from "../../../../Components/Misc/AssetThumbnail";
import Cover from "../../../../Components/Misc/Cover";
import AssetStatus from "../../../../Components/Misc/Status/AssetStatus/asset-status";
import { ModalContext } from "../../../../Components/Modals";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../../../Components/Modals/ModalConfirmAction/modal-confirm-action";
import ModalCreateShare from "../../../../Components/Modals/ModalCreateShare/modal-create-share";
import ModalEditMetadata, {
  ModalEditMetadataElementProps,
} from "../../../../Components/Modals/ModalEditMetadata/modal-edit-metadata";
import ModalExportAssetPdf, {
  ModalExportAssetPdfElementProps,
} from "../../../../Components/Modals/ModalExportAssetPdf/modal-export-asset-pdf";
import ModalPreviewBatonPdf, {
  ModalDocumentPreviewElementProps,
} from "../../../../Components/Modals/ModalPreview/modal-document-baton-pdf";
import ModalProviderHistory, {
  ModalProviderHistoryElementProps,
} from "../../../../Components/Modals/ModalProviderHistory/modal-provider-history";
import ModalSetFollowers, {
  ModalSetFollowersElementProps,
} from "../../../../Components/Modals/ModalSetFollowers/modal-set-followers";
import ModalStatusHistory, {
  ModalStatusHistoryElementProps,
} from "../../../../Components/Modals/ModalStatusHistory/modal-status-history";
import ModalUploadKdmFile, {
  ModalUploadKdmFileElementProps,
} from "../../../../Components/Modals/ModalUploadMediaFiles/modal-upload-kdm-file";
import {
  assetService,
  cacheService,
  dataService,
  setupService,
  thumbnailService,
  workflowService,
} from "../../../../Core/Api/services";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";
import {
  AdminAssetSingleDto,
  AdminAssetSinglePendingUploadDetails,
  AdminAssetSingleTopBarData,
  DbAssetObjectInfos,
} from "../../../../Core/Api/Types/db-asset-object-infos";
import { DbDeliveryMetadataExportInfos } from "../../../../Core/Api/Types/db-delivery-metadata-export-infos";
import { DbMetadataViewInfos } from "../../../../Core/Api/Types/db-metadata-view-infos";
import { ProviderHistoryModalDto } from "../../../../Core/Api/Types/db-object-provider-history-infos";
import { StatusHistoryModalDto } from "../../../../Core/Api/Types/db-object-status-history-infos";
import { Licences } from "../../../../Core/Api/Types/licences";
import { ObjectClass } from "../../../../Core/Api/Types/object-class";
import { ObjectLayer } from "../../../../Core/Api/Types/object-layer";
import { ObjectStatus } from "../../../../Core/Api/Types/object-status";
import { ObjectType } from "../../../../Core/Api/Types/object-type";
import { PlatformMode } from "../../../../Core/Api/Types/platform-mode";
import { UpdateMetadataFieldsRequest } from "../../../../Core/Api/Types/update-metadata-fields-request";
import { CreateNewDbMetadataValue, GetMetadataByTagName } from "../../../../Core/Utils/asset-object-utils";
import GlobalConst from "../../../../Core/Utils/Constants/globals";
import {
  GetObject,
  GetObjectHierarchyDto,
  IsObjectLoading,
  SetObjectMetadataViewIds,
} from "../../../../Redux/Reducers/redux-slice-asset";
import { useAppSelector } from "../../../../Redux/redux-store";
import { routePaths } from "../../../../Router/router";
import {
  AclMatch,
  AssetTypeAlphaColor,
  GetAssetObjectReadableType,
  IsContentDeletable,
  UserHasAcl,
  UserHasAcls,
  UserHasLicence,
} from "../../../../Utils/helpers";
import useAssetObjectUpdater from "../../../../Utils/Hooks/useAssetObjectUpdater";
import useBasketActions from "../../../../Utils/Hooks/useBasketActions";
import useCollectionActions from "../../../../Utils/Hooks/useCollectionActions";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import useJobActions from "../../../../Utils/Hooks/useJobActions";

interface IAssetHeadingProps {
  assetId: number;
  assetData: AdminAssetSingleDto | undefined;
  setReload: Dispatch<SetStateAction<number>>;
  collapsed?: boolean;
}

const AssetSingleHeading: React.FC<IAssetHeadingProps> = (props: IAssetHeadingProps) => {
  const { t } = useTranslation();
  const { assetId, assetData, setReload, collapsed = false } = props;

  const location = useLocation();
  const headingContainer = useRef<HTMLDivElement>(null);
  const isAssetPreviewInventoryPage = location.pathname.indexOf("preview") >= 25;

  // Enable websocket live update
  useAssetObjectUpdater(assetId);

  const asset = useSelector(GetObject);

  const AdamBaId = useMemo(() => GetMetadataByTagName(asset, "rc_ba_id")?.value, [asset]);
  const [assetQcData, setAssetQcData] = useState<AdminAssetSinglePendingUploadDetails | undefined>();

  const assetHierarchy = useSelector(GetObjectHierarchyDto);

  const isLoading = useAppSelector(IsObjectLoading);
  const theme = useTheme();

  const heroBackground = useMemo(() => {
    // set background color
    if (!asset) return theme.palette.background.paper;
    if (theme.palette.mode === "dark") {
      return `linear-gradient(225.52deg, ${AssetTypeAlphaColor(asset.type, 0.12)} 0%, ${AssetTypeAlphaColor(
        asset.type,
        0.02
      )} 50.71%, ${AssetTypeAlphaColor(asset.type, 0.08)} 100%)`;
    } else {
      //   setHeroBackground(
      //     `linear-gradient(225.52deg, ${assetTypeAlphaColor(asset.type, 0)} 0%, ${assetTypeAlphaColor(
      //       asset.type,
      //       0.05
      //     )} 50.71%, ${assetTypeAlphaColor(asset.type, 0.1)} 100%)`
      //   );
      return theme.palette.background.paper;
    }
  }, [theme, asset]);

  const platformMode = cacheService.GetPlatformMode();
  const isDriveOnly = UserHasAcl(AclTypes.aclDistributionDriveOnly);

  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const [hasCollapsed, setHasCollapsed] = useState<boolean>(false);
  const [shouldCollapse, setShouldCollapse] = useState<boolean>(isAssetPreviewInventoryPage);
  const [collapsedHeadHover, setCollapsedHeadHover] = useState<boolean>(false);

  useEffect(() => {
    // we record first collapse
    if (!hasCollapsed && collapsed) {
      setShouldCollapse(true);
      setHasCollapsed(true);
    }
  }, [collapsed]);

  const handleCollapsedHeadClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget || !shouldCollapse) return;
    setShouldCollapse(false);
    setCollapsedHeadHover(false);
    setHasCollapsed(false);
  };

  const openModalUploadDkdm = () => {
    if (!openModal || !asset) return;
    openModal<ModalUploadKdmFileElementProps>(ModalUploadKdmFile, {
      objectId: asset.id,
      onClose: (props: any) => {
        if (props.confirm) {
          // Reload asset
          setReload((prev) => ++prev);
          console.debug("Kdm upload ok => reload asset data");
        }
      },
    });
  };

  useEffect(() => {
    if (!asset) return;
    if (asset.status === ObjectStatus.ToBeQCed || asset.status === ObjectStatus.QualityCheckInProgress) {
      workflowService.GetUserTasksForAssetSingle(assetId).then((response) => {
        if (response) setAssetQcData(response);
      });
    }
  }, [asset]);

  return (
    <Box
      borderRadius={2}
      position="relative"
      sx={{
        backgroundColor: theme.palette.background.default,
        borderRadius: 2,
        mx: 1,
        flexShrink: 0,
        mt: 1,
        pl: 2,
        pr: 2,
        boxSizing: "content-box",
        overflow: "hidden",
        transition: theme.transitions.create(["background", "padding"], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.shorter,
        }),
        ...theme.helpers(theme).panelHighlights,
        ...(collapsedHeadHover && {
          background: theme.palette.background[200],
          pt: 1,
        }),
      }}
    >
      <Cover
        sx={{
          borderRadius: 2,
          position: "fixed",
          backgroundImage: isLoading
            ? `url(/static/images/admin-title-cover-${theme.palette.mode}.png)`
            : heroBackground,
        }}
        img={{ src: `` }}
        objectPosition="top"
      />
      {isLoading ? (
        <AssetSingleHeadingSkeleton />
      ) : (
        <Box sx={{ position: "relative" }}>
          <Fade in={!isLoading} timeout={1000}>
            <Collapse in={!shouldCollapse} collapsedSize={GlobalConst.HEADING_HEIGHT_COLLAPSED}>
              <Fade in={!shouldCollapse}>
                <Stack direction="column" gap={1}>
                  <Stack
                    justifyContent="space-between"
                    alignItems="flex-start"
                    direction="row"
                    gap={2}
                    sx={{ position: "relative" }}
                    pb={1}
                    pt={2}
                  >
                    <Stack direction="row" alignItems="flex-start" flexBasis={"50%"} gap={2}>
                      <AssetThumbnail sx={{ flexShrink: 0 }} type={assetData?.type || ObjectType.Video} size="medium" />
                      {!assetData || isLoading ? (
                        <Typography component="h2" variant="h6" color="text.tier">
                          {GlobalConst.LOADING_TEXT}{" "}
                        </Typography>
                      ) : (
                        <Stack alignItems="flex-start">
                          {platformMode === PlatformMode.Browser ||
                          assetData?.class === ObjectClass.Drive ||
                          assetData?.type === ObjectType.Folder ? (
                            <>
                              <Typography component="h1" variant="h3">
                                {assetData.parentTitleName && assetData.parentTitleLayer === ObjectLayer.Title
                                  ? assetData.parentTitleName + " | "
                                  : ""}
                                {assetData.name}
                              </Typography>
                              <Typography variant="body2" fontWeight={400} color="text.tier" mb={1.5}>
                                {!AdamBaId &&
                                  (assetData.layer !== ObjectLayer.Content
                                    ? GetAssetObjectReadableType(assetData.type)
                                    : t("common:content"))}
                                {AdamBaId ? `Id BA: ${AdamBaId}` : ` #${assetData.id}`}
                              </Typography>
                              {assetData.isAssetUploadable && <AssetStatus status={assetData.status} />}
                            </>
                          ) : (
                            <>
                              <Typography
                                component="h2"
                                variant="subtitle2"
                                fontWeight={400}
                                color="text.primary"
                                mb={1.5}
                              >
                                {assetData.parentTitleName ? assetData.parentTitleName + " | " : ""}
                                {assetData.name}
                              </Typography>
                              <Stack direction="row" spacing={1}>
                                {" "}
                                {assetData.isAssetUploadable && <AssetStatus status={assetData.status} />}
                                {assetData.isBypassed && (
                                  <Chip
                                    size={"medium"}
                                    variant={"outlined"}
                                    label={t("common:bypassed")}
                                    color={"warning"}
                                    sx={{ ml: 1 }}
                                  />
                                )}
                                {asset &&
                                  asset.type === ObjectType.DcpPackage &&
                                  GetMetadataByTagName(asset, "dcpIsCrypted")?.value && (
                                    <>
                                      {assetData.dcpDkdmObjectId !== null ? (
                                        <Chip
                                          label={t("chip:label.dkdmMasterKeyFound")}
                                          icon={<LockOpenOutlinedIcon />}
                                          variant="filled"
                                          size="medium"
                                          color="info"
                                        />
                                      ) : UserHasAcl(AclTypes.aclKdmUpload) &&
                                        UserHasLicence(Licences.DcpKDMFeature) ? (
                                        <Button
                                          size="small"
                                          sx={(theme) => ({ color: "text.secondary", height: 24 })}
                                          onClick={openModalUploadDkdm}
                                        >
                                          <KeyOutlined sx={{ mr: 1 }} />
                                          {t("button:uploadDkdmKey")}
                                        </Button>
                                      ) : (
                                        <Chip
                                          label={t("chip:label.noDkdmKey")}
                                          icon={<LockOutlined />}
                                          size="medium"
                                          variant="filled"
                                        />
                                      )}
                                    </>
                                  )}
                              </Stack>
                            </>
                          )}
                        </Stack>
                      )}
                    </Stack>
                    {assetData && (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <TopbarActions
                          assetId={assetData.id}
                          data={assetData.topbarData}
                          reload={() => setReload((prev) => ++prev)}
                        />
                      </Stack>
                    )}
                  </Stack>

                  {assetData &&
                    assetData.isAssetUploadable &&
                    platformMode !== PlatformMode.Adam &&
                    assetData.status === ObjectStatus.Deleted && (
                      <AssetHeroDeletionCard sx={{ mb: 1.5 }} deletionDetails={assetData.deleteDetails} />
                    )}
                  {assetData &&
                    assetData.isAssetUploadable &&
                    platformMode !== PlatformMode.Adam &&
                    assetData.status === ObjectStatus.Accepted &&
                    assetData.isBypassed && (
                      <AssetHeroBypassingCard sx={{ mb: 1.5 }} bypassDetails={assetData.bypassDetails} />
                    )}
                  {assetData &&
                    assetData.isAssetUploadable &&
                    platformMode !== PlatformMode.Adam &&
                    (assetData.status === ObjectStatus.Rejected || assetData.status === ObjectStatus.RejectedAuto) && (
                      <AssetHeroRejectionCard
                        sx={{ mb: 1.5 }}
                        assetId={assetData.id}
                        assetParentTitleId={assetData.parentTitleId}
                        rejectionDetails={assetData.rejectionDetails}
                      />
                    )}

                  {assetData &&
                    assetData.isAssetUploadable &&
                    assetData.status === ObjectStatus.New &&
                    platformMode !== PlatformMode.Adam && (
                      <AssetHeroRequestCard
                        sx={{ mb: 1.5 }}
                        assetId={assetData.id}
                        assetParentTitleId={assetData.parentTitleId}
                        requestDetails={assetData.requestDetails}
                      />
                    )}
                  {assetData && assetQcData && <AssetHeroPendingQcCard sx={{ mb: 1.5 }} qcDetails={assetQcData} />}
                  {assetData && <AssetTabbar assetId={assetId} />}
                </Stack>
              </Fade>
            </Collapse>
          </Fade>
          <Slide in={shouldCollapse} direction="up" container={headingContainer.current} mountOnEnter unmountOnExit>
            <Stack
              direction="row"
              justifySelf="stretch"
              justifyContent={"space-between"}
              sx={(t) => ({
                alignItems: "center",
                position: "absolute",
                left: 0,
                right: 0,
                top: 0,
                zIndex: 2,
                opacity: shouldCollapse ? 1 : 0,
              })}
              onClick={(e) => handleCollapsedHeadClick(e)}
            >
              <Stack sx={{ flexShrink: 1, minWidth: 0 }}>
                <AssetTabbar assetId={assetId} />
              </Stack>
              <Box
                onClick={(e) => handleCollapsedHeadClick(e)}
                onMouseEnter={() => setCollapsedHeadHover(true)}
                onMouseLeave={() => setCollapsedHeadHover(false)}
                sx={{ flexGrow: 1, height: GlobalConst.HEADING_HEIGHT_COLLAPSED, cursor: "pointer" }}
              />
              <Stack direction="row" gap={1}>
                {assetData && (
                  <TopbarActions
                    assetId={assetData.id}
                    data={assetData.topbarData}
                    reload={() => setReload((prev) => ++prev)}
                    minified
                  />
                )}
              </Stack>
            </Stack>
          </Slide>
        </Box>
      )}
    </Box>
  );
};

interface AdminAssetSingleTopbarActionsProps {
  assetId: number;
  data: AdminAssetSingleTopBarData;
  reload?: () => void;
  minified?: boolean;
}

const TopbarActions: FC<AdminAssetSingleTopbarActionsProps> = (props) => {
  const { t } = useTranslation();
  const { assetId, data, minified } = props;
  const { addObjectByIdToBasket } = useBasketActions();
  const { AddItemsToCollection } = useCollectionActions();
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const navigate = useNavigate();
  const asset = useSelector(GetObject);
  const enqueueCustomSnackbar = useCustomSnackbar();
  const [display, setDisplay] = useState<boolean>(true);
  const platformMode = cacheService.GetPlatformMode();
  const purgeDateMeta = "sysStorageDiskPurgeTargetDate";
  const adamPurgeTargetDate = cacheService.GetMetadata().find((x) => x.tagName === purgeDateMeta);

  const openModalMetadata = () => {
    if (!openModal) return;

    assetService.GetObjectById(assetId).then((res) => {
      if (res.success && res.graph) {
        openModal<ModalEditMetadataElementProps>(ModalEditMetadata, { objects: [res.graph] });
      } else enqueueCustomSnackbar(res.message || t("alert:error.assetNotFound"), "error");
    });
  };

  const openNewShareModal = useCallback(
    (object: DbAssetObjectInfos) => {
      if (!openModal) return;
      openModal(ModalCreateShare, { object: object });
    },
    [openModal]
  );

  const handleDeleteContent = () => {
    if (!openModal) {
      return;
    }

    openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
      displayComment: true,
      commentLabel: t("modal:comment.confirmDeleteContent"),
      title: t("modal:title.confirmDeleteContent"),
      onConfirm: handleDoDelete,
    });
  };

  const handleDoDelete = () => {
    assetService.DeleteObjectById(asset!.id).then((response) => {
      if (response.success) {
        enqueueCustomSnackbar(t("alert:success.contentDeleted"), "success");
        setDisplay(false);
      }
    });
  };

  const [anchorMoreEl, setAnchorMoreEl] = useState<null | HTMLElement>(null);
  const moreOpen = Boolean(anchorMoreEl);
  const handleMoreClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorMoreEl(event.currentTarget);
  };
  const handleMoreClose = () => {
    setAnchorMoreEl(null);
  };

  const [anchorToolsEl, setAnchorToolsEl] = useState<null | HTMLElement>(null);
  const toolsOpen = Boolean(anchorToolsEl);
  const handleToolsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorToolsEl(event.currentTarget);
  };
  const handleToolsClose = () => {
    setAnchorToolsEl(null);
  };

  const handleLockPurge = () => {
    if (!adamPurgeTargetDate) return;

    let meta = [
      CreateNewDbMetadataValue(
        adamPurgeTargetDate!.id,
        adamPurgeTargetDate.baseType!,
        adamPurgeTargetDate.tagName!,
        new Date(
          new Date(asset?.creationInfos?.date!).setDate(
            new Date(asset?.creationInfos?.date!).getDate() + GlobalConst.REPORTING_DAYS_BEFORE_PURGE_SOURCE_ADAM
          )
        )
      ),
    ];

    let request: any = null;
    let isLock: boolean = GetMetadataByTagName(asset, purgeDateMeta) != null;

    if (isLock)
      request = assetService.DeleteMetadataFields(
        new UpdateMetadataFieldsRequest({ objectId: assetId, metadata: meta })
      );
    else
      request = assetService.UpdateMetadataFields(
        new UpdateMetadataFieldsRequest({ objectId: assetId, metadata: meta })
      );

    request.then((res) => {
      if (res.success) {
        enqueueCustomSnackbar(
          isLock ? t("alert:success.unlockAssetPurge") : t("alert:success.lockAssetPurge"),
          "success"
        );
      } else
        enqueueCustomSnackbar(isLock ? t("alert:error.unlockAssetPurge") : t("alert:error.lockAssetPurge"), "error");
    });
  };

  return (
    <>
      <Button
        startIcon={<ShoppingBagOutlinedIcon />}
        onClick={() => addObjectByIdToBasket(assetId)}
        disabled={!data.userCanAddToBasket || asset?.layer === ObjectLayer.Head}
      >
        {t("common:order")}
      </Button>

      {GetMetadataByTagName(asset, "adamAssetType")?.value === "source" && (
        <IconButton
          title={GetMetadataByTagName(asset, purgeDateMeta) ? t("button:unlock") : t("button:lock")}
          color="neutral"
          size="small"
          onClick={handleLockPurge}
          disabled={false}
        >
          {GetMetadataByTagName(asset, purgeDateMeta) ? <LockOutlinedIcon /> : <LockOpenOutlinedIcon />}
        </IconButton>
      )}

      <IconButton
        color="neutral"
        size="small"
        onClick={() => openNewShareModal(asset!)}
        disabled={!data.userCanCreateShare}
      >
        <ShareOutlinedIcon />
      </IconButton>
      <IconButton
        onClick={() => {
          if (asset !== undefined) AddItemsToCollection([asset]);
        }}
        color="neutral"
        size="small"
      >
        <BookmarkBorderIcon />
      </IconButton>

      <IconButton
        aria-controls={moreOpen ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={moreOpen ? "true" : undefined}
        onClick={handleMoreClick}
        size="small"
        color="neutral"
      >
        <MoreHoriz />
      </IconButton>
      <Menu
        id="basic-menu"
        anchorEl={anchorMoreEl}
        open={moreOpen}
        onClose={handleMoreClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        {UserHasAcls([AclTypes.aclSuperUser, AclTypes.aclThumbnailClearCache], AclMatch.OneAtLeast) &&
          asset?.type === ObjectType.Image && (
            <MenuItem
              onClick={() => {
                thumbnailService.CleanCacheThumbRefByObjectId(asset.id).then((response) => {
                  if (response.success) enqueueCustomSnackbar(t("alert:primary.thumbnailCacheIsCleared"), "primary");
                });
              }}
            >
              <ListItemIcon>
                <RefreshIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              </ListItemIcon>
              <ListItemText>{t("common:clearCache")}</ListItemText>
            </MenuItem>
          )}
        {asset?.layer === ObjectLayer.Content && IsContentDeletable(asset) && display && (
          <MenuItem onClick={handleDeleteContent}>
            <ListItemIcon sx={{ fontSize: 16, color: "error.main" }}>{t("common:deleteContent")}</ListItemIcon>
            <ListItemText sx={{ color: "error.main" }}>{t("common:deleteContent")}</ListItemText>
          </MenuItem>
        )}
        {platformMode === PlatformMode.Adam && GetMetadataByTagName(asset!, "adamAssetType")?.value === "trailer" && (
          <MenuItem
            onClick={() => {
              openModal &&
                openModal<ModalSetFollowersElementProps>(ModalSetFollowers, {
                  objectIds: [asset!.id],
                  objectType: "asset",
                  currentFollowersIds:
                    asset!.followersInfos?.map((x) => x.userId).filter((id): id is number => id !== null) || [],
                });
            }}
          >
            <ListItemIcon>
              <GroupsOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
            </ListItemIcon>
            <ListItemText>{t("common:setFollowers")}</ListItemText>
          </MenuItem>
        )}
        <HistoryMenuItems assetId={assetId} statusHistory={data.statusHistory} providerHistory={data.providerHistory} />
        <ExportMenuItems
          assetId={assetId}
          userCanExportXml={data.userCanExportXml}
          userCanExportPdf={data.userCanExportPdf}
        />
      </Menu>

      {asset && asset?.type === ObjectType.Folder && UserHasAcl(AclTypes.aclSuperUser) && !minified && (
        <SetMetadataViewFilteringMenuButton asset={asset} />
      )}

      {!minified && (
        <>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />
        </>
      )}

      {asset &&
        [
          ObjectType.Video,
          ObjectType.Audio,
          ObjectType.Subtitle,
          ObjectType.ImageSequence,
          ObjectType.ScribeSubtitleProject,
          ObjectType.TimetoolSequence,
        ].includes(asset.type) &&
        !minified && (
          <>
            <Button
              aria-controls={toolsOpen ? "basic-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={toolsOpen ? "true" : undefined}
              onClick={handleToolsClick}
              size="small"
              color="primary"
              variant="outlined"
              startIcon={<TroubleshootIcon />}
              endIcon={<ExpandMoreRoundedIcon />}
            >
              {t("button:tools")}
            </Button>
            <Menu open={toolsOpen} anchorEl={anchorToolsEl} onClose={handleToolsClose}>
              {asset &&
                (asset.type === ObjectType.Video ||
                  asset.type === ObjectType.Subtitle ||
                  asset.type === ObjectType.ScribeSubtitleProject) &&
                UserHasAcl(AclTypes.aclScriberAccess) && (
                  <MenuItem onClick={() => navigate(`${routePaths.scribetool.path}/${assetId}`)}>
                    {t("common:scribetool")}
                  </MenuItem>
                )}
              {asset &&
                (asset.type === ObjectType.Video ||
                  asset.type === ObjectType.Audio ||
                  asset.type === ObjectType.Subtitle ||
                  asset.type === ObjectType.ImageSequence) && (
                  <MenuItem onClick={() => navigate(`${routePaths.toolsQc.path}/${assetId}`)}>
                    {t("common:qcTool")}
                  </MenuItem>
                )}
              {asset && asset.type === ObjectType.TimetoolSequence && (
                <MenuItem onClick={() => navigate(`${routePaths.timetool.path}/${assetId}`)}>
                  {t("menu:item.timetool")}
                </MenuItem>
              )}
            </Menu>
          </>
        )}

      {!minified ? (
        <Button
          color="primary"
          variant="contained"
          startIcon={<EditOutlinedIcon />}
          onClick={openModalMetadata}
          disabled={!data.userCanEditMetadata}
        >
          {t("common:editMetadata")}
        </Button>
      ) : (
        <IconButton color="primary" onClick={openModalMetadata} size="small" disabled={!data.userCanEditMetadata}>
          <EditOutlinedIcon />
        </IconButton>
      )}
    </>
  );
};

const SetMetadataViewFilteringMenuButton: FC<{
  asset: DbAssetObjectInfos;
}> = (props) => {
  const { t } = useTranslation();
  const [anchorMoreEl, setAnchorMoreEl] = useState<null | HTMLElement>(null);
  const [metadataViews, setMetadataViews] = useState<DbMetadataViewInfos[]>([]);
  const [message, setMessage] = useState<string>(GlobalConst.LOADING_TEXT);
  const dispatch = useDispatch();

  // expand menu
  const handleMoreClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setAnchorMoreEl(e.currentTarget);
    setupService.GetAllMetadataViewsInfo().then((data) => {
      if (!data.success || data.data === null) {
        setMessage(t("menu:item.errorWhileLoading"));
        return;
      }
      setMetadataViews(data.data!);
    });
  };
  const handleMoreClose = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setAnchorMoreEl(null);
  };
  const openMore = Boolean(anchorMoreEl);

  const onClick = (metadataViewId: number) => {
    assetService.ToggleMetadataViewFilterById(props.asset.id, metadataViewId).then((data) => {
      if (!data.success) return;
      dispatch(SetObjectMetadataViewIds(metadataViewId));
    });
  };

  return (
    <>
      <Button id="export-menu-button" endIcon={<ExpandMoreRoundedIcon />} onClick={handleMoreClick} sx={{ mr: 1 }}>
        {t("button:setViewFilter")}
      </Button>
      <Menu
        id="export-menu"
        MenuListProps={{
          "aria-labelledby": "export-menu-button",
        }}
        anchorEl={anchorMoreEl}
        open={openMore}
        onClose={handleMoreClose}
        anchorPosition={{
          top: 12,
          left: 0,
        }}
      >
        {metadataViews.length !== 0 ? (
          metadataViews.map((x, index) => (
            <MenuItem key={index} onClick={() => onClick(x.id)} selected={props.asset.viewIds?.includes(x.id)}>
              {x.name}
            </MenuItem>
          ))
        ) : (
          <MenuItem key={-1}>{message}</MenuItem>
        )}
      </Menu>
    </>
  );
};

const HistoryMenuItems: FC<{
  assetId: number;
  statusHistory: StatusHistoryModalDto[];
  providerHistory: ProviderHistoryModalDto[];
}> = ({ assetId, statusHistory, providerHistory }) => {
  const { t } = useTranslation();
  // expand menu
  const handleMoreClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
  };
  const handleMoreClose = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
  };

  // Modals
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const openModalStatusHistory = () => {
    if (!openModal) return;
    openModal<ModalStatusHistoryElementProps>(ModalStatusHistory, {
      assetId,
      historyData: statusHistory,
    });
  };

  const openModalProviderHistory = () => {
    if (!openModal) return;
    openModal<ModalProviderHistoryElementProps>(ModalProviderHistory, {
      assetId,
      historyData: providerHistory,
    });
  };
  return (
    <>
      <MenuItem onClick={openModalStatusHistory}>{t("menu:item.statusChanges")}</MenuItem>
      <MenuItem onClick={openModalProviderHistory}>{t("menu:item.providerChanges")}</MenuItem>
    </>
  );
};

const ExportMenuItems: FC<{ assetId: number; userCanExportXml: boolean; userCanExportPdf: boolean }> = ({
  assetId,
  userCanExportXml,
  userCanExportPdf,
}) => {
  const { t } = useTranslation();
  const enqueueCustomSnackbar = useCustomSnackbar();
  const jobActions = useJobActions();

  const [exports, setExports] = useState<DbDeliveryMetadataExportInfos[]>([]);

  useEffect(() => {
    if (UserHasAcls([AclTypes.aclSuperUser, AclTypes.aclMetadataExport], AclMatch.OneAtLeast) && exports.length === 0)
      dataService.GetAssetsMetadataExports().then((response) => setExports(response.exports || []));
  }, []);

  // xml export
  const handleExportAsXml = () => {
    assetService.ExportAssetAsXml(assetId, true).then((response) => {
      enqueueCustomSnackbar(response.message, response.success ? "success" : "error");
    });
  };

  // pdf export
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const openModalDownloadPdf = () => {
    if (!openModal) return;
    openModal<ModalExportAssetPdfElementProps>(ModalExportAssetPdf, {
      assetId,
    });
  };

  // export sessions
  const handleExportBySession = (templateId: number) => {
    enqueueCustomSnackbar(t("alert:primary.downloadStarted"), "primary");
    dataService
      .DownloadExportAssetByTemplate(templateId, assetId)
      .then(() => {})
      .catch(() => enqueueCustomSnackbar(t("alert:error.downloadFailed"), "error"));
  };

  const handleOpenPdfModal = (objectId) => {
    if (!openModal) return;

    openModal<ModalDocumentPreviewElementProps>(ModalPreviewBatonPdf, {
      objectId: objectId,
    });
  };

  return (
    <>
      <MenuItem onClick={handleExportAsXml} disabled={!userCanExportXml}>
        {t("menu:item.exportAsXml")}
      </MenuItem>
      <MenuItem onClick={openModalDownloadPdf} disabled={!userCanExportPdf}>
        {t("menu:item.exportAsPdf")}
      </MenuItem>
      <MenuItem onClick={() => handleOpenPdfModal(assetId)} disabled={!userCanExportPdf}>
        {t("common:exportBatonPdf")}
      </MenuItem>
      {exports.map((_export) => (
        <MenuItem key={_export.id} onClick={() => handleExportBySession(_export.id)} disabled={!userCanExportPdf}>
          {_export.name}
        </MenuItem>
      ))}
    </>
  );
};

const AssetTabbar: FC<{ assetId: number; minified?: boolean }> = ({ assetId, minified = false }) => {
  const { t } = useTranslation();

  return (
    <Tabbar
      sx={(theme) =>
        minified
          ? {
              height: "100%",
              ".MuiTabs-flexContainer": {
                height: "100%",
              },
            }
          : {}
      }
      tabs={[
        [
          {
            label: t("common:metadata"),
            to: `${routePaths.asset.path}/${assetId}`,
            matchingPaths: [`${routePaths.asset.path}/${assetId}/:type`],
          },
          {
            label: t("common:preview"),
            to: `${routePaths.asset.path}/${assetId}/${routePaths.assetPreview.path}`,
          },
          {
            label: t("route:label.hierarchy"),
            to: `${routePaths.asset.path}/${assetId}/${routePaths.assetHierarchy.path}`,
          },
          {
            label: t("common:mediaFiles"),
            to: `${routePaths.asset.path}/${assetId}/${routePaths.assetMediaFiles.path}`,
          },
          {
            label: t("common:actions"),
            to: `${routePaths.asset.path}/${assetId}/${routePaths.assetActions.path}`,
            //hide: !UserHasAcls([AclTypes.aclSuperUser, AclTypes.aclAdmin], AclMatch.OneAtLeast),
          },
          {
            label: t("common:shares"),
            to: `${routePaths.asset.path}/${assetId}/${routePaths.assetShares.path}`,
            hide: !UserHasAcl(AclTypes.aclShareView),
          },
          {
            label: t("common:orders"),
            to: `${routePaths.asset.path}/${assetId}/${routePaths.assetOrders.path}`,
            hide: !UserHasAcl(AclTypes.aclSectionOrderAccess),
          },
          {
            label: t("common:activities"),
            to: `${routePaths.asset.path}/${assetId}/${routePaths.assetActivities.path}`,
          },
          {
            label: t("route:label.quotas"),
            to: `${routePaths.asset.path}/${assetId}/${routePaths.assetQuotas.path}`,
            hide: !UserHasAcls(
              [AclTypes.aclQuotaObjectView, AclTypes.aclSuperUser, AclTypes.aclAdmin],
              AclMatch.OneAtLeast
            ),
          },
        ],
      ]}
    />
  );
};

const AssetSingleHeadingSkeleton = () => {
  const theme = useTheme();
  return (
    <Stack direction="row" alignItems="flex-start" gap={2} height={184}>
      <Skeleton width={60} height={60} />
      <Stack alignItems="flex-start">
        <Skeleton width={200} height={60} />
        <Skeleton width={60} height={20} />
      </Stack>
    </Stack>
  );
};

export default AssetSingleHeading;
